/**
 * Branded DatePicker — clay button trigger + Calendar in a Radix Popover.
 * Drop-in replacement for `<Input type="date">`: same `value` ('YYYY-MM-DD' or
 * '') and an `onChange(value)` callback, so call sites barely change.
 *
 * e2e: the trigger keeps the field's `data-testid`; the day/year/month cells
 * inside expose their own testids (see Calendar). A `pickDate` fixture helper
 * drives caption → year → month → day.
 */
import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDisplay(value: string): string | null {
  if (!value || value.length !== 10) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  fromYear?: number;
  toYear?: number;
  placeholder?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  fromYear,
  toYear,
  placeholder = 'Pick a date',
  id,
  className,
  disabled,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const display = formatDisplay(value);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          data-testid={testId}
          aria-label={ariaLabel}
          data-placeholder={display ? undefined : ''}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-input border-2 border-input bg-card px-4 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast',
            'focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15 data-[state=open]:border-primary data-[state=open]:ring-4 data-[state=open]:ring-primary/15',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className={cn('truncate', !display && 'text-muted-foreground')}>{display ?? placeholder}</span>
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-50 overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-popover text-popover-foreground shadow-clay-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
          )}
        >
          <Calendar
            value={value}
            min={min}
            max={max}
            fromYear={fromYear}
            toYear={toYear}
            onSelect={(v) => {
              onChange(v);
              setOpen(false);
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
